import type { GetMarketplaceJobsResponseDto } from "@workspace/dto/job/get-marketplace-jobs";
import { useCallback, useMemo, useRef } from "react";
import Supercluster from "supercluster";

export type MarketplaceJob = GetMarketplaceJobsResponseDto["jobs"][number];

type JobMarker = {
  id: string;
  longitude: number;
  latitude: number;
  job: MarketplaceJob;
};

export type JobClusterPointProperties = {
  id: string;
  job: MarketplaceJob;
};

export type JobClusterLeaf =
  Supercluster.PointFeature<JobClusterPointProperties>;

export type JobClusterFeature =
  Supercluster.ClusterFeature<Supercluster.AnyProps>;

export type JobClusterResult = JobClusterFeature | JobClusterLeaf;

const MAX_CLUSTER_ZOOM = 20;
const CLUSTER_RADIUS = 40;
const MIN_CLUSTER_POINTS = 2;

export function useCluster(
  markers: JobMarker[] | undefined,
  zoom: number,
  bounds?: [number, number, number, number]
) {
  const superclusterRef = useRef<Supercluster<
    JobClusterPointProperties,
    Supercluster.AnyProps
  > | null>(null);

  const points = useMemo(() => {
    if (!markers) {
      return [];
    }

    return markers.map((marker) => ({
      type: "Feature" as const,
      geometry: {
        type: "Point" as const,
        coordinates: [marker.longitude, marker.latitude],
      },
      properties: {
        id: marker.id,
        job: marker.job,
      },
    }));
  }, [markers]);

  const clusters = useMemo<JobClusterResult[]>(() => {
    if (!superclusterRef.current) {
      superclusterRef.current = new Supercluster<
        JobClusterPointProperties,
        Supercluster.AnyProps
      >({
        radius: CLUSTER_RADIUS,
        maxZoom: MAX_CLUSTER_ZOOM,
        minPoints: MIN_CLUSTER_POINTS,
      });
    }

    superclusterRef.current.load(points);

    // Use provided bounds or fall back to world bounds
    const clusterBounds = bounds || [
      -180, // west
      -90, // south
      180, // east
      90, // north
    ];

    const clusterList = superclusterRef.current.getClusters(
      clusterBounds,
      Math.floor(zoom)
    );

    return clusterList;
  }, [points, zoom, bounds]);

  const getClusterExpansionZoom = useCallback((clusterId: number) => {
    if (!superclusterRef.current) {
      return 0;
    }
    return superclusterRef.current.getClusterExpansionZoom(clusterId);
  }, []);

  const getClusterLeaves = useCallback(
    (clusterId: number, limit = 10, offset = 0) => {
      if (!superclusterRef.current) {
        return [];
      }
      return superclusterRef.current.getLeaves(clusterId, limit, offset);
    },
    []
  );

  return {
    clusters,
    getClusterExpansionZoom,
    getClusterLeaves,
  };
}
